import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';
import { File as FileGlyph, Globe } from 'lucide-react';
import { useMemo, type KeyboardEvent, type MouseEvent } from 'react';

import { ConnectedServiceIcon } from '../../atoms/ConnectedServiceIcon';
import { GridSensitivityChip } from '../../atoms/GridSensitivityChip';
import { TruncatedCellText } from '../../atoms/TruncatedCellText';
import { DocumentTypeIcon, FileTypeIcon } from '../../icons';
import {
  resolveFileTypeIcon,
  resolveLocationIcon,
  resolveUserTypeBadge,
} from '../../lib/access-legitimacy-resolvers';
import { humanizeLabel } from '../../lib/humanize-label';
import { cn } from '../../lib/utils';
import { LabeledIconCell } from '../../molecules/LabeledIconCell';
import { Pagination } from '../../molecules/Pagination';
import { SortableColumnHeader } from '../../molecules/SortableColumnHeader';
import { TopicListPopover } from '../../molecules/TopicListPopover';
import { UserTypeBadge } from '../../molecules/UserTypeBadge';

/**
 * Sort direction for the date-only sort (design v6 Decision #12). The endpoint
 * sorts by event time only; there is no `sortBy` and no per-column sort.
 */
export type AccessLegitimacySortDir = 'asc' | 'desc';

/**
 * One detected sensitivity (entity label + severity score), or `null`.
 * Re-declared locally — `packages/ui` must not import `@dawn/feature-config`
 * (agnosticism firewall); this is structurally equal to the wire
 * `AccessLegitimacySensitivity`.
 */
export interface AccessLegitimacySensitivity {
  entity: string;
  severity: number;
}

/** One action-history entry, structurally equal to the wire entry. */
export interface AccessLegitimacyActionHistoryEntry {
  dateTime: string;
  userType: string;
  actor: string;
  action: string;
}

/**
 * The grid's row type — re-declared locally to be **structurally equal** to the
 * wire `AccessLegitimacyEvent` (design v6 §3.3, BE-shape-direct). Every taxonomy
 * field is a bare lowercase label (ADR 0002); the event carries the full drawer
 * payload inline so the drawer needs no second request. `packages/ui` does NOT
 * import the wire type — the structural match is enforced at the app boundary
 * when the app passes wire `rows` to this grid (design Open Question 1).
 *
 * `id` MUST be unique across `rows[]` — drives React key + data-testid.
 */
export interface AccessLegitimacyEvent {
  id: string;
  dateTime: string;
  actor: string;
  // The file's name in its source system. Optional + nullable to match the wire
  // contract: an older API omits it and a source may have none; both render "—".
  fileName?: string | null;
  // The file's link in its source system, shown only in the detail drawer.
  fileUrl?: string | null;
  // Nullable to match the wire contract (`@dawn/feature-config`): the curated
  // audit source may have no user-type for an event. Cell renders a DashCell.
  userType: string | null;
  documentType: string | null;
  documentSubject: string | null;
  topics: string[];
  fileType: string | null;
  location: string | null;
  sensitivity: AccessLegitimacySensitivity | null;
  illegitimacyReason: string | null;
  summary: string | null;
  ddcClassification: string[];
  scopes: string[];
  namesInFile: string[];
  actionHistory: AccessLegitimacyActionHistoryEntry[];
}

/**
 * Column widths per Figma `5:5799` (unchanged from v1), plus File Name at
 * Actor's 180px (TDEV-1125, no Figma frame). Used two ways: as
 * proportions, so the 10 columns fill any responsive widget width, and — via
 * their sum as the table's `min-width` — as each column's px floor, below which
 * header labels stop being readable. Each value is the width its column's
 * header label was drawn for, so keep them in sync with the labels.
 */
const COL_WIDTHS_PX: ReadonlyArray<number> = [
  180, // actor
  180, // fileName
  96, // userType
  132, // documentType
  180, // documentSubject
  160, // documentTopic
  108, // fileType
  132, // sensitivity
  132, // location
  136, // dateTime
] as const;
const COL_WIDTHS_TOTAL_PX = COL_WIDTHS_PX.reduce((sum, w) => sum + w, 0);

/**
 * Column widths for the `<colgroup>`, as percentages of the table width so the
 * grid fills any widget width and keeps the Figma proportions at every size.
 *
 * The px floor is not expressed here — it comes from the table's `min-width`
 * (see the `<table>` below). Because these percentages are derived from
 * `COL_WIDTHS_PX`, a table clamped at `COL_WIDTHS_TOTAL_PX` gives every column
 * back exactly its design px width, which is the width its header label was
 * drawn for.
 */
const COL_WIDTHS_PERCENT: ReadonlyArray<string> = COL_WIDTHS_PX.map(
  (w) => `${((w / COL_WIDTHS_TOTAL_PX) * 100).toFixed(4)}%`
);

export interface AccessLegitimacyGridProps {
  /** Pre-sliced page rows (the server already filtered / sorted / paged). */
  rows: ReadonlyArray<AccessLegitimacyEvent>;
  /** Total filtered row count, pre-paging (informational). */
  total: number;
  /** Active date-sort direction. */
  sortDir: AccessLegitimacySortDir;
  /** Fires with the flipped direction when the Date & time header is toggled. */
  onSortDirChange: (next: AccessLegitimacySortDir) => void;
  /** 1-indexed page. */
  page: number;
  /** Total page count (`>= 1`). */
  pageCount: number;
  onPageChange: (next: number) => void;
  /** Formats an ISO date string for the Date & time column. */
  formatDateTime: (iso: string) => string;
  /** Popover title template for the documentTopic +N chip panel. */
  topicPopoverTitle?: (totalCount: number) => string;
  /** Chip accessible label template. */
  topicChipAriaLabel?: (additionalCount: number) => string;
  /**
   * Fires when a row is clicked or activated via Enter/Space. When omitted, the
   * grid renders rows as non-interactive (no role, tabIndex, handlers, cursor).
   */
  onRowClick?: (id: string) => void;
  /** Id of the currently-selected event (drives the selected-row visual). */
  selectedEventId?: string | null;
  className?: string;
}

const DEFAULT_TOPIC_POPOVER_TITLE = (n: number): string => `Document Topic (${n})`;
const DEFAULT_TOPIC_CHIP_ARIA_LABEL = (n: number): string => `plus ${n} more topics`;

const INTERACTIVE_TAGS = new Set(['BUTTON', 'A', 'INPUT', 'TEXTAREA', 'SELECT']);

/** The dash placeholder for a null taxonomy / text value. */
const EMPTY = '—';

function originatesFromInteractiveDescendant(
  event: MouseEvent<HTMLTableRowElement> | KeyboardEvent<HTMLTableRowElement>
): boolean {
  const currentTarget = event.currentTarget;
  let el: HTMLElement | null = event.target instanceof HTMLElement ? event.target : null;
  while (el !== null && el !== currentTarget) {
    if (INTERACTIVE_TAGS.has(el.tagName)) return true;
    if (el.getAttribute('role') === 'button') return true;
    const ti = el.getAttribute('tabindex');
    if (ti !== null && ti !== '-1') return true;
    el = el.parentElement;
  }
  return false;
}

/** Muted dash cell for a null value. */
function DashCell({ testId }: { testId: string }) {
  return (
    <span data-testid={testId} className="inline-block text-[10px] leading-[1.2] text-grey-400">
      {EMPTY}
    </span>
  );
}

/**
 * AccessLegitimacyGrid — 10-column controlled-mode TanStack Table consuming the
 * live wire event shape (design v6 §3.3). Fully server-driven: the parent owns
 * filter/sort/page state; this grid renders the page it receives.
 *
 * Sort is date-only (Decision #12): only the Date & time header is interactive
 * and toggles `sortDir` (asc ↔ desc). All other columns render plain labels.
 *
 * Taxonomy labels are humanized at render; the Sensitivity column renders the
 * severity-driven `GridSensitivityChip` (no slug round-trip); file-type /
 * location icons resolve through the defensive resolvers (unknown label → a
 * generic fallback glyph, never a crash).
 */
export function AccessLegitimacyGrid({
  rows,
  total: _total,
  sortDir,
  onSortDirChange,
  page,
  pageCount,
  onPageChange,
  formatDateTime,
  topicPopoverTitle = DEFAULT_TOPIC_POPOVER_TITLE,
  topicChipAriaLabel = DEFAULT_TOPIC_CHIP_ARIA_LABEL,
  onRowClick,
  selectedEventId,
  className,
}: AccessLegitimacyGridProps) {
  const handleDateSortToggle = () => {
    onSortDirChange(sortDir === 'asc' ? 'desc' : 'asc');
  };

  const columns = useMemo<ColumnDef<AccessLegitimacyEvent>[]>(() => {
    return [
      {
        id: 'actor',
        header: () => <PlainHeader label="Actor" />,
        cell: ({ row }) => <TruncatedCellText data-testid="cell-actor" text={row.original.actor} />,
      },
      {
        id: 'fileName',
        header: () => <PlainHeader label="File Name" />,
        cell: ({ row }) =>
          row.original.fileName ? (
            <TruncatedCellText data-testid="cell-file-name" text={row.original.fileName} />
          ) : (
            <DashCell testId="cell-file-name" />
          ),
      },
      {
        id: 'userType',
        header: () => <PlainHeader label="User type" />,
        cell: ({ row }) => {
          const userType = row.original.userType;
          if (userType === null) return <DashCell testId="cell-user-type" />;
          const badge = resolveUserTypeBadge(userType);
          return (
            <div data-testid="cell-user-type">
              <UserTypeBadge
                size="sm"
                type={badge.type}
                isPrivileged={badge.isExternal}
                aria-label={humanizeLabel(userType)}
              />
            </div>
          );
        },
      },
      {
        id: 'documentType',
        header: () => <PlainHeader label="Document type" />,
        cell: ({ row }) => {
          const label = row.original.documentType;
          if (label === null) return <DashCell testId="cell-document-type" />;
          return (
            <div data-testid="cell-document-type" className="inline-flex min-w-0 max-w-full">
              <span className="inline-flex min-w-0 items-center gap-[2px] rounded-[2px] bg-grey-100 px-1 py-0.5">
                <DocumentTypeIcon size={12} className="shrink-0 text-grey-600" />
                <span
                  title={humanizeLabel(label)}
                  className="truncate whitespace-nowrap text-[10px] leading-[1.2] text-dawn-black"
                >
                  {humanizeLabel(label)}
                </span>
              </span>
            </div>
          );
        },
      },
      {
        id: 'documentSubject',
        header: () => <PlainHeader label="Document Subject" />,
        cell: ({ row }) =>
          row.original.documentSubject === null ? (
            <DashCell testId="cell-document-subject" />
          ) : (
            <TruncatedCellText
              data-testid="cell-document-subject"
              text={row.original.documentSubject}
            />
          ),
      },
      {
        id: 'documentTopic',
        header: () => <PlainHeader label="Document Topic" />,
        cell: ({ row }) => {
          const topics = row.original.topics;
          if (topics.length === 0) return <DashCell testId="cell-document-topic" />;
          return (
            <TopicListPopover
              primary={humanizeLabel(topics[0]!)}
              additional={topics.slice(1).map(humanizeLabel)}
              popoverTitle={topicPopoverTitle}
              chipAriaLabel={topicChipAriaLabel}
            />
          );
        },
      },
      {
        id: 'fileType',
        header: () => <PlainHeader label="File type" />,
        cell: ({ row }) => {
          const label = row.original.fileType;
          if (label === null) return <DashCell testId="cell-file-type" />;
          const iconName = resolveFileTypeIcon(label);
          return (
            <div data-testid="cell-file-type">
              <LabeledIconCell
                icon={
                  iconName !== null ? (
                    <FileTypeIcon name={iconName} size={12} />
                  ) : (
                    <FileGlyph
                      width={12}
                      height={12}
                      className="text-grey-500"
                      aria-hidden="true"
                    />
                  )
                }
                label={humanizeLabel(label)}
                className="gap-1"
                labelClassName="text-[10px] leading-[1.2]"
              />
            </div>
          );
        },
      },
      {
        id: 'sensitivity',
        header: () => <PlainHeader label="Sensitivity" />,
        cell: ({ row }) => (
          <div data-testid="cell-sensitivity">
            <GridSensitivityChip sensitivity={row.original.sensitivity} />
          </div>
        ),
      },
      {
        id: 'location',
        header: () => <PlainHeader label="Location" />,
        cell: ({ row }) => {
          const label = row.original.location;
          if (label === null) return <DashCell testId="cell-location" />;
          const brand = resolveLocationIcon(label);
          return (
            <div data-testid="cell-location">
              <LabeledIconCell
                icon={
                  brand !== null ? (
                    <ConnectedServiceIcon service={brand} size={12} />
                  ) : (
                    <Globe width={12} height={12} className="text-grey-500" aria-hidden="true" />
                  )
                }
                label={humanizeLabel(label)}
                className="gap-1"
                labelClassName="text-[10px] leading-[1.2]"
              />
            </div>
          );
        },
      },
      {
        id: 'dateTime',
        header: () => (
          <SortableColumnHeader
            label="Date & time"
            sortDirection={sortDir}
            onSortToggle={handleDateSortToggle}
            inTableHeaderCell
          />
        ),
        cell: ({ row }) => (
          <span
            data-testid="cell-date-time"
            className="inline-block whitespace-nowrap text-[10px] capitalize leading-[1.2] text-dawn-black"
          >
            {formatDateTime(row.original.dateTime)}
          </span>
        ),
      },
    ];
    // `handleDateSortToggle` is recreated every render and intentionally NOT
    // listed; `onSortDirChange` is listed so callers passing an unstable
    // callback still see fresh closures when the function identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDir, onSortDirChange, formatDateTime, topicPopoverTitle, topicChipAriaLabel]);

  // Map external sort shape ↔ TanStack `SortingState` (date column only).
  const sortingState: SortingState = useMemo(
    () => [{ id: 'dateTime', desc: sortDir === 'desc' }],
    [sortDir]
  );

  // TanStack `PaginationState` is 0-indexed; external `page` is 1-indexed.
  // `pageSize` is the current slice length (cosmetic — pagination is manual and
  // driven by the explicit `pageCount`).
  const paginationState: PaginationState = useMemo(
    () => ({ pageIndex: Math.max(0, page - 1), pageSize: rows.length }),
    [page, rows.length]
  );

  const noopSortingChange: OnChangeFn<SortingState> = () => {};
  const noopPaginationChange: OnChangeFn<PaginationState> = () => {};

  const table = useReactTable({
    data: rows as AccessLegitimacyEvent[],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount,
    state: { sorting: sortingState, pagination: paginationState },
    onSortingChange: noopSortingChange,
    onPaginationChange: noopPaginationChange,
  });

  return (
    <div
      className={cn('flex min-h-0 flex-1 flex-col gap-4', className)}
      data-testid="access-legitimacy-grid"
    >
      {/*
        The one and only scroll region on the page (TDEV-960): the card caps
        itself at the layout content slot, so the rows scroll here while the
        filter rail and the pager stay put. When no ancestor bounds the height
        this shrinks to nothing to shrink and never engages.
      */}
      <div className="min-h-0 flex-1 overflow-y-auto" data-testid="access-legitimacy-scroll-region">
        {/*
          `border-separate` (not `border-collapse`): collapsed borders belong to
          the table grid, not the cell, so the header divider would scroll away
          from the sticky `<th>` in Chromium.

          `min-width` is what gives every column a floor. All ten widths are
          percentages, so on a narrow window they scale down together until the
          header labels are unreadable ("Document Subject" collapsing to
          "Docume Subject") and the non-truncatable Sensitivity chip spills into
          Location. Clamping the table at the sum of the design widths hands each
          column back exactly the px width its label was drawn for, and the
          scroll region takes the difference on the x axis. It has to sit on the
          table: `min-width` applies to neither a `<col>` nor a cell under
          `table-fixed`, and `width: max(%, px)` on a `<col>` is normalised away
          — all three measured in-browser as no-ops.
        */}
        <table
          className="w-full table-fixed border-separate border-spacing-0"
          style={{ minWidth: `${COL_WIDTHS_TOTAL_PX}px` }}
        >
          <colgroup>
            {COL_WIDTHS_PERCENT.map((w, idx) => (
              <col key={idx} style={{ width: w }} />
            ))}
          </colgroup>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => {
              const lastIdx = headerGroup.headers.length - 1;
              return (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, idx) => {
                    const edgeClass =
                      idx === 0 ? 'pl-0 pr-2' : idx === lastIdx ? 'pl-2 pr-0' : 'px-2';
                    // Only the Date & time column is sortable; expose `aria-sort`
                    // on its `<th>` (where the spec places it) and leave the
                    // others `none`.
                    const isDateCol = header.column.id === 'dateTime';
                    const ariaSort: 'ascending' | 'descending' | 'none' = isDateCol
                      ? sortDir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none';
                    return (
                      <th
                        key={header.id}
                        aria-sort={ariaSort}
                        className={cn(
                          'sticky top-0 z-10 border-b border-grey-100 bg-white pb-1 pt-2 text-left align-middle',
                          edgeClass
                        )}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody>
            <tr aria-hidden="true">
              <td colSpan={COL_WIDTHS_PX.length} className="h-3 p-0" />
            </tr>
            {table.getRowModel().rows.map((row) => {
              const visibleCells = row.getVisibleCells();
              const lastIdx = visibleCells.length - 1;
              const isInteractive = onRowClick !== undefined;
              const isSelected =
                selectedEventId !== undefined &&
                selectedEventId !== null &&
                row.original.id === selectedEventId;
              const interactiveProps = isInteractive
                ? {
                    tabIndex: 0,
                    onClick: (e: MouseEvent<HTMLTableRowElement>) => {
                      if (originatesFromInteractiveDescendant(e)) return;
                      onRowClick(row.original.id);
                    },
                    onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
                      if (originatesFromInteractiveDescendant(e)) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(row.original.id);
                      }
                    },
                  }
                : {};
              return (
                <tr
                  key={row.original.id}
                  data-testid={`access-legitimacy-row-${row.original.id}`}
                  className={cn(
                    'h-7',
                    isInteractive && 'cursor-pointer transition-colors',
                    isSelected && 'bg-[#D88B3D]/30'
                  )}
                  aria-selected={isSelected ? true : undefined}
                  {...interactiveProps}
                >
                  {visibleCells.map((cell, idx) => {
                    const edgeClass =
                      idx === 0 ? 'pl-0 pr-2' : idx === lastIdx ? 'pl-2 pr-0' : 'px-2';
                    return (
                      <td key={cell.id} className={cn('h-7 py-0 align-middle', edgeClass)}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex shrink-0 justify-end border-t border-grey-100 pt-[10px]">
        <Pagination
          page={page}
          pageCount={pageCount}
          onFirst={() => onPageChange(1)}
          onPrev={() => onPageChange(Math.max(1, page - 1))}
          onNext={() => onPageChange(Math.min(pageCount, page + 1))}
          onLast={() => onPageChange(pageCount)}
        />
      </div>
    </div>
  );
}

AccessLegitimacyGrid.displayName = 'AccessLegitimacyGrid';

/** Plain (non-sortable) column header label, matching the sortable header's type ramp. */
function PlainHeader({ label }: { label: string }) {
  return (
    <span className="font-outfit text-[10px] font-medium capitalize leading-[1.2] text-grey-500">
      {label}
    </span>
  );
}
